import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCreateRecipe, useRecipes, useSubscription } from '../hooks/useSupabase';
import { isPremium, FREE_RECIPE_LIMIT } from '../lib/billing';
import { useImageUpload } from '../hooks/useImageUpload';
import { trackEvent } from '../lib/analytics';
import { DIETARY_OPTIONS } from '../lib/dietary';
import { supabase } from '../lib/supabase';

const CATEGORIES = ['breakfast', 'lunch', 'dinner', 'snack'];


const SECTIONS = [
  { value: 'produce', label: 'Produce' },
  { value: 'meat', label: 'Meat & Seafood' },
  { value: 'dairy', label: 'Dairy & Eggs' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'pantry', label: 'Pantry' },
  { value: 'spices', label: 'Spices' },
];

const UNIT_SUGGESTIONS = ['cup','tbsp','tsp','oz','lb','fl oz','','clove','can','bunch'];

const emptyIngredient = () => ({ item: '', amount: '', unit: '', section: 'pantry' });
const emptyStep = () => ({ text: '', time: '' });

export default function RecipeCreate() {
  const navigate = useNavigate();
  const { householdId, currentMember } = useAuth();
  const { createRecipe, loading } = useCreateRecipe(householdId, currentMember?.id);
  const { recipes: existingRecipes } = useRecipes(householdId);
  const { uploading, uploadRecipeImage, deleteRecipeImage } = useImageUpload();
  const fileInputRef = useRef(null);

  // Premium gating (UI state only — RLS/Edge Functions enforce server-side)
  const { subscription } = useSubscription(householdId);
  const premium = isPremium(subscription);
  const customRecipeCount = (existingRecipes || []).filter((r) => r.household_id).length;
  const atRecipeCap = !premium && customRecipeCount >= FREE_RECIPE_LIMIT;

  // Photo
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoError, setPhotoError] = useState(null);

  // Basics
  const [name, setName] = useState('');
  const [category, setCategory] = useState('dinner');
  const [cuisine, setCuisine] = useState('');
  const [description, setDescription] = useState('');

  // Timing & Servings
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [servings, setServings] = useState('4');

  // Ingredients
  const [ingredients, setIngredients] = useState([emptyIngredient()]);

  // Steps
  const [steps, setSteps] = useState([emptyStep()]);

  // Tags
  const [dietaryTags, setDietaryTags] = useState([]);
  const [tagsText, setTagsText] = useState('');

  // Nutrition (collapsible)
  const [showNutrition, setShowNutrition] = useState(false);
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  const [fiber, setFiber] = useState('');

  // Errors
  const [errors, setErrors] = useState({});

  // URL Import
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(false);

  // ─── URL Import ───────────────────────────────────────────────────────────────

  const handleImportFromUrl = async () => {
    if (!importUrl.trim() || importing) return;

    setImporting(true);
    setImportError(null);
    setImportSuccess(false);

    try {
      const { data, error } = await supabase.functions.invoke('parse-recipe-url', {
        body: { url: importUrl.trim() },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const recipe = data?.recipe;
      if (!recipe) throw new Error('No recipe data returned');

      // Populate form fields
      if (recipe.name) setName(recipe.name);
      if (recipe.description) setDescription(recipe.description);
      if (recipe.cuisine) setCuisine(recipe.cuisine);
      if (recipe.prepTime) setPrepTime(String(recipe.prepTime));
      if (recipe.cookTime) setCookTime(String(recipe.cookTime));
      if (recipe.servings) setServings(String(recipe.servings));

      // Map category
      if (recipe.category) {
        const cat = recipe.category.toLowerCase();
        if (CATEGORIES.includes(cat)) {
          setCategory(cat);
        } else if (cat.includes('breakfast')) {
          setCategory('breakfast');
        } else if (cat.includes('lunch')) {
          setCategory('lunch');
        } else if (cat.includes('dinner') || cat.includes('main')) {
          setCategory('dinner');
        } else if (cat.includes('snack') || cat.includes('dessert')) {
          setCategory('snack');
        }
      }

      // Ingredients
      if (recipe.ingredients?.length > 0) {
        setIngredients(recipe.ingredients.map(ing => ({
          item: ing.item || '',
          amount: ing.amount ? String(ing.amount) : '',
          unit: ing.unit || '',
          section: ing.section || 'pantry',
        })));
      }

      // Steps
      if (recipe.steps?.length > 0) {
        setSteps(recipe.steps.map(s => ({
          text: s.text || '',
          time: s.time || '',
        })));
      }

      // Nutrition
      if (recipe.nutrition && Object.keys(recipe.nutrition).length > 0) {
        setShowNutrition(true);
        if (recipe.nutrition.calories) setCalories(String(recipe.nutrition.calories));
        if (recipe.nutrition.protein_g) setProtein(String(recipe.nutrition.protein_g));
        if (recipe.nutrition.carbs_g) setCarbs(String(recipe.nutrition.carbs_g));
        if (recipe.nutrition.fat_g) setFat(String(recipe.nutrition.fat_g));
        if (recipe.nutrition.fiber_g) setFiber(String(recipe.nutrition.fiber_g));
      }

      // Tags
      if (recipe.tags?.length > 0) {
        const dietary = recipe.tags.filter(t =>
          DIETARY_OPTIONS.some(opt => opt.value === t.toLowerCase())
        );
        const other = recipe.tags.filter(t =>
          !DIETARY_OPTIONS.some(opt => opt.value === t.toLowerCase())
        );
        setDietaryTags(dietary.map(t => t.toLowerCase()));
        setTagsText(other.join(', '));
      }

      setImportSuccess(true);
      trackEvent('Recipe Imported', { source: new URL(importUrl).hostname });
    } catch (err) {
      setImportError(err.message || 'Failed to import recipe');
    } finally {
      setImporting(false);
    }
  };

  // ─── Ingredient helpers ─────────────────────────────────────────────────────

  const updateIngredient = (idx, field, value) => {
    setIngredients(prev => prev.map((ing, i) => i === idx ? { ...ing, [field]: value } : ing));
  };

  const addIngredient = () => setIngredients(prev => [...prev, emptyIngredient()]);

  const removeIngredient = (idx) => {
    if (ingredients.length <= 1) return;
    setIngredients(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── Step helpers ───────────────────────────────────────────────────────────

  const updateStep = (idx, field, value) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const addStep = () => setSteps(prev => [...prev, emptyStep()]);

  const removeStep = (idx) => {
    if (steps.length <= 1) return;
    setSteps(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── Photo helpers ─────────────────────────────────────────────────────────

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError(null);
    setPhotoFile(file);
    const url = URL.createObjectURL(file);
    setPhotoPreview(url);
  };

  const removePhoto = () => {
    setPhotoFile(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
    setPhotoError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Recipe name is required';

    const validIngredients = ingredients.filter(i => i.item.trim());
    if (validIngredients.length === 0) newErrors.ingredients = 'At least 1 ingredient is required';

    const validSteps = steps.filter(s => s.text.trim());
    if (validSteps.length === 0) newErrors.steps = 'At least 1 step is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    // Upload photo if present
    let imageUrl = null;
    const recipeId = crypto.randomUUID();

    if (photoFile && householdId) {
      const { url, error: uploadErr } = await uploadRecipeImage(photoFile, householdId, recipeId);
      if (uploadErr) {
        setPhotoError(uploadErr);
        return;
      }
      imageUrl = url;
    }

    const freeTags = tagsText.split(',').map(t => t.trim()).filter(Boolean);
    const tags = [...new Set([...dietaryTags, ...freeTags])];

    const nutrition = {};
    if (showNutrition) {
      if (calories) nutrition.calories = parseInt(calories) || 0;
      if (protein) nutrition.protein_g = parseInt(protein) || 0;
      if (carbs) nutrition.carbs_g = parseInt(carbs) || 0;
      if (fat) nutrition.fat_g = parseInt(fat) || 0;
      if (fiber) nutrition.fiber_g = parseInt(fiber) || 0;
    }

    const recipe = {
      id: recipeId,
      name: name.trim(),
      category,
      cuisine: cuisine.trim(),
      description: description.trim(),
      prep_time_min: parseInt(prepTime) || 0,
      cook_time_min: parseInt(cookTime) || 0,
      servings: parseInt(servings) || 4,
      ingredients: validIngredients.map(i => ({
        item: i.item.trim(),
        amount: parseFloat(i.amount) || 0,
        unit: i.unit.trim(),
        section: i.section,
      })),
      steps: validSteps.map(s => ({
        text: s.text.trim(),
        time: s.time.trim() || null,
      })),
      tags,
      nutrition,
      ...(imageUrl && { image_url: imageUrl }),
    };

    const { error } = await createRecipe(recipe);
    if (error) {
      // Clean up orphaned image
      if (imageUrl) deleteRecipeImage(imageUrl);
      // RLS rejects inserts past the free cap — client state can be stale
      // (e.g. another family member just added a recipe)
      const message = /row-level security/i.test(error.message)
        ? `Your household reached the free limit of ${FREE_RECIPE_LIMIT} custom recipes. Upgrade to Premium for unlimited.`
        : error.message;
      setErrors({ submit: message });
      return;
    }

    trackEvent('Recipe Created', { category });

    navigate('/');
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  const inputClass = 'w-full px-3 py-2 rounded-xl border border-hairline bg-surface font-body text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent';
  const labelClass = 'block font-body text-xs font-medium text-ink-soft mb-1';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-lg text-ink-soft hover:text-ink hover:bg-surface transition-colors"
          aria-label="Go back"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="font-display text-xl font-semibold text-ink">New recipe</h2>
      </div>

      {/* ── Import from URL ───────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-lime to-mint rounded-2xl p-5 border border-hairline space-y-3">
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-lime">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          <h3 className="font-display text-base font-semibold text-ink">Import from URL</h3>
          {!premium && (
            <span className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full bg-ink/10 font-body text-[10px] font-bold text-ink">
              🔒 Premium
            </span>
          )}
        </div>
        <p className="font-body text-xs text-ink-soft">
          Paste a link from any recipe website to auto-fill the form
        </p>
        {!premium ? (
          <button
            type="button"
            onClick={() => navigate('/upgrade')}
            className="w-full py-2.5 rounded-xl bg-panel text-ink font-body text-sm font-semibold hover:bg-surface transition-colors"
          >
            Unlock with Premium →
          </button>
        ) : (
        <>
        <div className="flex gap-2">
          <input
            type="url"
            value={importUrl}
            onChange={(e) => { setImportUrl(e.target.value); setImportError(null); setImportSuccess(false); }}
            placeholder="https://example.com/recipe/..."
            className="flex-1 px-3 py-2 rounded-xl border border-hairline bg-panel font-body text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent"
          />
          <button
            type="button"
            onClick={handleImportFromUrl}
            disabled={!importUrl.trim() || importing}
            className="px-4 py-2 rounded-xl bg-lime text-canvas font-body text-sm font-semibold hover:bg-lime disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {importing ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Importing...
              </>
            ) : 'Import'}
          </button>
        </div>
        {importError && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-red-500 shrink-0 mt-0.5">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p className="font-body text-xs text-red-600">{importError}</p>
          </div>
        )}
        {importSuccess && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-mint/10 border border-mint/40">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-mint">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <p className="font-body text-xs text-mint">Recipe imported! Review and edit the details below.</p>
          </div>
        )}
        </>
        )}
        <p className="font-body text-[10px] text-ink-muted">
          Works with AllRecipes, Food Network, Tasty, NYT Cooking, BBC Good Food, and most recipe blogs
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-surface" />
        <span className="font-body text-xs text-ink-muted">or enter manually</span>
        <div className="h-px flex-1 bg-surface" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* ── Basics ────────────────────────────────────────────────────────── */}
        <div className="bg-panel rounded-2xl p-5 border border-hairline space-y-4">
          <h3 className="font-display text-base font-semibold text-ink">Basics</h3>

          <div>
            <label className={labelClass}>Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Grandma's Chili"
              className={inputClass}
            />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className={labelClass}>Category</label>
            <div className="flex gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  className={`flex-1 py-2 rounded-xl font-body font-medium text-sm capitalize transition-colors ${
                    category === cat
                      ? 'bg-lime text-canvas'
                      : 'bg-surface text-ink-soft border border-hairline'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>Cuisine</label>
            <input
              type="text"
              value={cuisine}
              onChange={(e) => setCuisine(e.target.value)}
              placeholder="e.g. Mexican, Italian"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description of the dish"
              rows={2}
              className={inputClass}
            />
          </div>
        </div>

        {/* ── Photo ────────────────────────────────────────────────────────── */}
        <div className="bg-panel rounded-2xl p-5 border border-hairline space-y-4">
          <h3 className="font-display text-base font-semibold text-ink">Photo</h3>

          {photoPreview ? (
            <div className="relative">
              <img
                src={photoPreview}
                alt="Recipe preview"
                className="w-full h-48 object-cover rounded-xl"
              />
              <button
                type="button"
                onClick={removePhoto}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                aria-label="Remove photo"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ) : !premium ? (
            <button
              type="button"
              onClick={() => navigate('/upgrade')}
              className="w-full h-32 rounded-xl border-2 border-dashed border-hairline flex flex-col items-center justify-center gap-1 text-ink-muted hover:border-lime/40 transition-colors"
            >
              <span className="text-xl">🔒</span>
              <span className="font-body text-sm font-medium">Recipe photos are a Premium feature</span>
              <span className="font-body text-xs text-lime font-semibold">Unlock with Premium →</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-32 rounded-xl border-2 border-dashed border-hairline flex flex-col items-center justify-center gap-2 text-ink-muted hover:border-lime/40 hover:text-lime transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              <span className="font-body text-sm font-medium">Add a photo</span>
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoSelect}
            className="hidden"
          />

          {photoError && <p className="text-sm text-red-600">{photoError}</p>}
        </div>

        {/* ── Timing & Servings ─────────────────────────────────────────────── */}
        <div className="bg-panel rounded-2xl p-5 border border-hairline space-y-4">
          <h3 className="font-display text-base font-semibold text-ink">Timing & Servings</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Prep (min)</label>
              <input
                type="number"
                min="0"
                value={prepTime}
                onChange={(e) => setPrepTime(e.target.value)}
                placeholder="15"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Cook (min)</label>
              <input
                type="number"
                min="0"
                value={cookTime}
                onChange={(e) => setCookTime(e.target.value)}
                placeholder="30"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Servings</label>
              <input
                type="number"
                min="1"
                value={servings}
                onChange={(e) => setServings(e.target.value)}
                placeholder="4"
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* ── Ingredients ───────────────────────────────────────────────────── */}
        <div className="bg-panel rounded-2xl p-5 border border-hairline space-y-4">
          <h3 className="font-display text-base font-semibold text-ink">Ingredients</h3>
          {errors.ingredients && <p className="text-sm text-red-600">{errors.ingredients}</p>}

          {ingredients.map((ing, idx) => (
            <div key={idx} className="bg-surface rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-body text-xs font-medium text-ink-soft">Ingredient {idx + 1}</span>
                {ingredients.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeIngredient(idx)}
                    className="min-w-[44px] min-h-[44px] flex items-center justify-center text-ink-muted hover:text-red-500 transition-colors"
                    aria-label="Remove ingredient"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>

              <input
                type="text"
                value={ing.item}
                onChange={(e) => updateIngredient(idx, 'item', e.target.value)}
                placeholder="Item name"
                className={inputClass}
              />

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={ing.amount}
                    onChange={(e) => updateIngredient(idx, 'amount', e.target.value)}
                    placeholder="Amt"
                    className={inputClass}
                  />
                </div>
                <div>
                  <input
                    type="text"
                    list="unit-suggestions"
                    value={ing.unit}
                    onChange={(e) => updateIngredient(idx, 'unit', e.target.value)}
                    placeholder="Unit"
                    className={inputClass}
                  />
                </div>
                <div>
                  <select
                    value={ing.section}
                    onChange={(e) => updateIngredient(idx, 'section', e.target.value)}
                    className={inputClass}
                  >
                    {SECTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}

          <datalist id="unit-suggestions">
            {UNIT_SUGGESTIONS.map(u => <option key={u} value={u} />)}
          </datalist>

          <button
            type="button"
            onClick={addIngredient}
            className="w-full py-3 rounded-xl border-2 border-dashed border-hairline text-ink-muted hover:border-lime/40 hover:text-lime font-body font-medium transition-colors"
          >
            + Add ingredient
          </button>
        </div>

        {/* ── Steps ─────────────────────────────────────────────────────────── */}
        <div className="bg-panel rounded-2xl p-5 border border-hairline space-y-4">
          <h3 className="font-display text-base font-semibold text-ink">Steps</h3>
          {errors.steps && <p className="text-sm text-red-600">{errors.steps}</p>}

          {steps.map((step, idx) => (
            <div key={idx} className="bg-surface rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-body text-xs font-medium text-ink-soft">Step {idx + 1}</span>
                {steps.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeStep(idx)}
                    className="min-w-[44px] min-h-[44px] flex items-center justify-center text-ink-muted hover:text-red-500 transition-colors"
                    aria-label="Remove step"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>

              <textarea
                value={step.text}
                onChange={(e) => updateStep(idx, 'text', e.target.value)}
                placeholder="Describe this step..."
                rows={2}
                className={inputClass}
              />

              <input
                type="text"
                value={step.time}
                onChange={(e) => updateStep(idx, 'time', e.target.value)}
                placeholder="Time hint (e.g. 5 min)"
                className={inputClass}
              />
            </div>
          ))}

          <button
            type="button"
            onClick={addStep}
            className="w-full py-3 rounded-xl border-2 border-dashed border-hairline text-ink-muted hover:border-lime/40 hover:text-lime font-body font-medium transition-colors"
          >
            + Add step
          </button>
        </div>

        {/* ── Tags ──────────────────────────────────────────────────────────── */}
        <div className="bg-panel rounded-2xl p-5 border border-hairline space-y-4">
          <h3 className="font-display text-base font-semibold text-ink">Tags</h3>
          <div>
            <label className={labelClass}>Dietary</label>
            <div className="flex flex-wrap gap-2">
              {DIETARY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDietaryTags(prev =>
                    prev.includes(opt.value)
                      ? prev.filter(v => v !== opt.value)
                      : [...prev, opt.value]
                  )}
                  className={`px-3 py-1.5 rounded-full font-body text-xs font-medium transition-colors ${
                    dietaryTags.includes(opt.value)
                      ? 'bg-lime text-canvas'
                      : 'bg-surface text-ink-soft border border-hairline'
                  }`}
                >
                  {opt.emoji} {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Other tags (comma-separated)</label>
            <input
              type="text"
              value={tagsText}
              onChange={(e) => setTagsText(e.target.value)}
              placeholder="e.g. kid-friendly, quick, healthy"
              className={inputClass}
            />
          </div>
        </div>

        {/* ── Nutrition (collapsible) ───────────────────────────────────────── */}
        <div className="bg-panel rounded-2xl border border-hairline overflow-hidden">
          <button
            type="button"
            onClick={() => setShowNutrition(!showNutrition)}
            className="w-full px-5 py-4 flex items-center justify-between"
          >
            <h3 className="font-display text-base font-semibold text-ink">Nutrition</h3>
            <div className="flex items-center gap-2">
              <span className="font-body text-xs text-ink-muted">Optional</span>
              <svg
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                className={`w-4 h-4 text-ink-soft transition-transform ${showNutrition ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </button>

          {showNutrition && (
            <div className="px-5 pb-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Calories</label>
                  <input type="number" min="0" value={calories} onChange={(e) => setCalories(e.target.value)} placeholder="0" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Protein (g)</label>
                  <input type="number" min="0" value={protein} onChange={(e) => setProtein(e.target.value)} placeholder="0" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Carbs (g)</label>
                  <input type="number" min="0" value={carbs} onChange={(e) => setCarbs(e.target.value)} placeholder="0" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Fat (g)</label>
                  <input type="number" min="0" value={fat} onChange={(e) => setFat(e.target.value)} placeholder="0" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Fiber (g)</label>
                  <input type="number" min="0" value={fiber} onChange={(e) => setFiber(e.target.value)} placeholder="0" className={inputClass} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Submit ─────────────────────────────────────────────────────────── */}
        {errors.submit && (
          <p className="text-sm text-red-600 text-center">{errors.submit}</p>
        )}

        {!premium && (
          <div className={`rounded-xl px-4 py-3 border ${atRecipeCap ? 'bg-surface border-lime/40' : 'bg-panel border-hairline'}`}>
            <div className="flex items-center justify-between">
              <p className="font-body text-xs font-medium text-ink-soft">
                {customRecipeCount} of {FREE_RECIPE_LIMIT} free custom recipes used
              </p>
              {atRecipeCap && (
                <button
                  type="button"
                  onClick={() => navigate('/upgrade')}
                  className="font-body text-xs font-bold text-lime"
                >
                  Go unlimited →
                </button>
              )}
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-surface overflow-hidden">
              <div
                className="h-full rounded-full bg-lime transition-all"
                style={{ width: `${Math.min(100, (customRecipeCount / FREE_RECIPE_LIMIT) * 100)}%` }}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || uploading || atRecipeCap}
          className="w-full py-3 rounded-xl bg-lime text-canvas font-body text-sm font-semibold hover:bg-lime active:bg-lime transition-colors disabled:opacity-50"
        >
          {atRecipeCap ? 'Free recipe limit reached' : uploading ? 'Uploading photo...' : loading ? 'Saving...' : 'Save recipe'}
        </button>
      </form>
    </div>
  );
}
